import { defineStore } from "pinia";

import CalculatorPresetsApi from "@/services/api/calculatorPresetsApi";

import { getCompanyId } from "@/utils/userUtils";

export const useCalculatorPresetsStore = defineStore(
  "calculatorPresets",
  {
    state: () => ({
      presets: [],
      selectedPreset: null,
      sources: [],
      operations: [],
      companyId: getCompanyId(),
      dataIsLoading: false,
      isSubmitting: false,
      pagination: {
        page: 1,
        pages: 1,
        series: [],
        next: null,
        prev: null,
        count: 0,
        limit: 40,
      },
    }),

    getters: {
      getPresetById: (state) => (id) =>
        state.presets.find((preset) => String(preset.id) === String(id)) ||
        null,
    },

    actions: {
      normalizeResponseData(response) {
        return response.data.result || response.data || [];
      },

      applyPagination(response) {
        const meta = response.data.pagination_meta;
        if (!meta) return;

        this.pagination = {
          page: meta.page,
          pages: meta.pages,
          series: meta.series,
          next: meta.next,
          prev: meta.prev,
          count: meta.count,
          limit: meta.limit,
        };
      },

      // Обновляет или добавляет пресет в список и синхронизирует selectedPreset.
      // unshift при отсутствии — чтобы только что созданный пресет был вверху списка.
      replacePreset(preset) {
        const index = this.presets.findIndex((p) => p.id === preset.id);
        if (index === -1) {
          this.presets.unshift(preset);
        } else {
          this.presets.splice(index, 1, preset);
        }

        if (this.selectedPreset?.id === preset.id) {
          this.selectedPreset = preset;
        }
      },

      async fetchPresets(params = {}) {
        this.dataIsLoading = true;
        try {
          const response = await CalculatorPresetsApi.index(
            this.companyId,
            params
          );
          this.presets = this.normalizeResponseData(response);
          this.applyPagination(response);
        } finally {
          this.dataIsLoading = false;
        }
      },

      async fetchPreset(id) {
        this.dataIsLoading = true;
        try {
          const response = await CalculatorPresetsApi.show(
            this.companyId,
            id
          );
          this.selectedPreset = this.normalizeResponseData(response);
          this.replacePreset(this.selectedPreset);
          return this.selectedPreset;
        } finally {
          this.dataIsLoading = false;
        }
      },

      async fetchSources() {
        const response = await CalculatorPresetsApi.sources(this.companyId);
        this.sources = this.normalizeResponseData(response);
      },

      async fetchOperations() {
        const response = await CalculatorPresetsApi.operations(this.companyId);
        this.operations = this.normalizeResponseData(response);
      },

      async fetchFormDictionaries() {
        await Promise.all([this.fetchSources(), this.fetchOperations()]);
      },

      async createPreset(payload) {
        this.isSubmitting = true;
        try {
          const response = await CalculatorPresetsApi.create(
            this.companyId,
            payload
          );
          const preset = this.normalizeResponseData(response);
          this.replacePreset(preset);
          return preset;
        } finally {
          this.isSubmitting = false;
        }
      },

      async updatePreset(id, payload) {
        this.isSubmitting = true;
        try {
          const response = await CalculatorPresetsApi.update(
            this.companyId,
            id,
            payload
          );
          const preset = this.normalizeResponseData(response);
          this.replacePreset(preset);
          return preset;
        } finally {
          this.isSubmitting = false;
        }
      },

      async deletePreset(id) {
        this.isSubmitting = true;
        try {
          await CalculatorPresetsApi.destroy(this.companyId, id);
          this.presets = this.presets.filter((preset) => preset.id !== id);
          if (this.selectedPreset?.id === id) this.selectedPreset = null;
        } finally {
          this.isSubmitting = false;
        }
      },

      async enablePreset(id) {
        this.isSubmitting = true;
        try {
          const response = await CalculatorPresetsApi.enable(
            this.companyId,
            id
          );
          const preset = this.normalizeResponseData(response);
          this.replacePreset(preset);
          return preset;
        } finally {
          this.isSubmitting = false;
        }
      },

      async disablePreset(id) {
        this.isSubmitting = true;
        try {
          const response = await CalculatorPresetsApi.disable(
            this.companyId,
            id
          );
          const preset = this.normalizeResponseData(response);
          this.replacePreset(preset);
          return preset;
        } finally {
          this.isSubmitting = false;
        }
      },

      async resetToDefault(id) {
        this.isSubmitting = true;
        try {
          const response = await CalculatorPresetsApi.resetToDefault(
            this.companyId,
            id
          );
          const preset = this.normalizeResponseData(response);
          this.replacePreset(preset);
          if (this.selectedPreset?.id === id) this.selectedPreset = preset;
          return preset;
        } finally {
          this.isSubmitting = false;
        }
      },

      async setPrimaryPreset(id) {
        this.isSubmitting = true;
        try {
          const response = await CalculatorPresetsApi.setPrimary(
            this.companyId,
            id
          );
          const preset = this.normalizeResponseData(response);
          this.presets = this.presets.map((item) => ({
            ...item,
            primary: item.id === preset.id,
          }));
          this.replacePreset(preset);
          return preset;
        } finally {
          this.isSubmitting = false;
        }
      },
    },
  }
);
